import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme, TOKENS } from '../context/ThemeContext';

const TOPIC_ICONS = {
  'Apex Fundamentals':    '📘',
  'Data Querying':        '🔍',
  'Data Manipulation':    '💾',
  'Object-Oriented Apex': '🏗️',
  'Apex Triggers':        '⚡',
  'Asynchronous Apex':    '⏳',
  'Apex Testing':         '🧪',
  'Error Handling':       '🚨',
  'Custom Metadata':      '📋',
  'Working with JSON':    '🔄',
  'REST Callouts':        '🌐',
  'Mocks & Testing':      '🧪',
  'Platform Events':      '📡',
  'LWC Fundamentals':     '⚡',
  'LWC Advanced':         '🚀',
};

const SECTIONS = [
  { id: 'overview',    label: 'Overview'        },
  { id: 'why',         label: 'Why it matters'  },
  { id: 'tools',       label: 'Tools'           },
  { id: 'code',        label: 'Code example'    },
  { id: 'gotchas',     label: 'Gotchas'         },
  { id: 'quiz',        label: 'Test yourself'   },
  { id: 'docs',        label: 'Docs'            },
];

// ── Flip card ────────────────────────────────────────────────────────────────
function GotchaCard({ g, c }) {
  const [flipped, setFlipped] = useState(false);

  const errorBg    = '#1a0808';
  return (
    <div onClick={() => setFlipped(!flipped)}
      style={{ height: '180px', cursor: 'pointer', perspective: '1000px' }}>
      <div style={{
        position: 'relative', width: '100%', height: '100%',
        transition: 'transform 0.5s', transformStyle: 'preserve-3d',
        transform: flipped ? 'rotateY(180deg)' : 'none',
      }}>
        {/* Front */}
        <div style={{
          position: 'absolute', width: '100%', height: '100%',
          background: c.card, border: `1px solid ${c.border}`,
          borderRadius: '10px', padding: '16px', boxSizing: 'border-box',
          backfaceVisibility: 'hidden', display: 'flex',
          flexDirection: 'column', justifyContent: 'space-between',
        }}>
          <div>
            <p style={{ fontSize: '11px', color: c.warning, margin: '0 0 8px', fontWeight: 600 }}>⚠ Common mistake</p>
            <p style={{ fontSize: '13px', fontWeight: 600, color: c.text, margin: '0 0 10px', lineHeight: 1.5 }}>{g.problem}</p>
            <code style={{ fontSize: '11px', color: c.error, background: errorBg, padding: '4px 8px', borderRadius: '4px', display: 'block', fontFamily: 'monospace', lineHeight: 1.5 }}>{g.error}</code>
          </div>
          <p style={{ fontSize: '11px', color: c.muted, margin: 0, textAlign: 'right' }}>flip to see fix →</p>
        </div>
        {/* Back */}
        <div style={{
          position: 'absolute', width: '100%', height: '100%',
          background: c.card, border: `1px solid ${c.accent}`,
          borderRadius: '10px', padding: '16px', boxSizing: 'border-box',
          backfaceVisibility: 'hidden', transform: 'rotateY(180deg)',
          overflow: 'hidden',
        }}>
          <p style={{ fontSize: '11px', fontWeight: 600, color: c.accent, margin: '0 0 5px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Why it happens</p>
          <p style={{ fontSize: '12px', color: c.secondary, margin: '0 0 10px', lineHeight: 1.6 }}>{g.why}</p>
          <p style={{ fontSize: '11px', fontWeight: 600, color: c.accent, margin: '0 0 5px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Fix</p>
          <p style={{ fontSize: '12px', color: c.text, margin: 0, lineHeight: 1.6 }}>{g.fix}</p>
        </div>
      </div>
    </div>
  );
}

// ── Test Yourself ────────────────────────────────────────────────────────────
function TestYourself({ questions, c }) {
  const [open, setOpen]         = useState(null);
  const [revealed, setRevealed] = useState(0);

  const toggle = (i) => {
    const wasOpen = open === i;
    setOpen(wasOpen ? null : i);
    if (!wasOpen) setRevealed(r => r + 1);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <span style={{ fontSize: '12px', color: c.secondary }}>{revealed} / {questions.length} revealed</span>
      </div>
      <div style={{ background: c.border, borderRadius: '2px', height: '3px', marginBottom: '20px' }}>
        <div style={{ width: `${(revealed / questions.length) * 100}%`, height: '3px', background: c.accent, borderRadius: '2px', transition: 'width 0.3s' }} />
      </div>
      {questions.map((item, i) => (
        <div key={i} style={{
          background: c.card, border: `1px solid ${open === i ? c.accent : c.border}`,
          borderRadius: '8px', marginBottom: '8px', overflow: 'hidden',
          transition: 'border-color 0.15s', boxShadow: c.shadow,
        }}>
          <div onClick={() => toggle(i)}
            style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '14px 16px', cursor: 'pointer' }}>
            <div style={{
              width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0,
              background: open === i ? c.accentBg : c.bg,
              border: `1px solid ${open === i ? c.accent : c.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '11px', fontWeight: 700, color: open === i ? c.accent : c.secondary,
              transition: 'all 0.15s',
            }}>{i + 1}</div>
            <p style={{ fontSize: '13px', fontWeight: 500, color: c.text, margin: 0, flex: 1, lineHeight: 1.6 }}>{item.q}</p>
            <span style={{ color: c.muted, fontSize: '12px', flexShrink: 0 }}>{open === i ? '▲' : '▼'}</span>
          </div>
          {open === i && (
            <div style={{ padding: '0 16px 16px 52px', borderTop: `1px solid ${c.border}` }}>
              <p style={{ fontSize: '13px', color: c.secondary, lineHeight: 1.75, margin: '14px 0 0' }}>{item.a}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Syntax highlight ─────────────────────────────────────────────────────────
function CodeBlock({ code, c }) {
  const editorBg = c.theme === 'light' ? '#f8f9fd' : '#070810';
  const lines = code.split('\n').map((line, i) => {
    const highlighted = line
      .replace(/(\/\/.*$)/g,   '<span style="color:#4b5563">$1</span>')
      .replace(/\b(public|private|static|void|class|return|new|if|else|for|null|true|false|implements|extends|try|catch|finally)\b/g, '<span style="color:#60a5fa">$1</span>')
      .replace(/@\w+/g,        '<span style="color:#4f8ef7">$&</span>')
      .replace(/\b(String|Integer|Decimal|Boolean|Id|List|Map|Set|Date|DateTime|Account|Contact|Opportunity)\b/g, '<span style="color:#a5b4fc">$1</span>')
      .replace(/('[^']*')/g,   '<span style="color:#86efac">$1</span>');
    return <span key={i} dangerouslySetInnerHTML={{ __html: highlighted + '\n' }} />;
  });
  return (
    <div style={{
      background: editorBg, border: `1px solid ${c.border}`,
      borderRadius: '10px', overflow: 'auto',
      fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
      fontSize: '13px', lineHeight: 1.9, color: c.secondary,
      transition: 'background 0.25s, border-color 0.25s',
    }}>
      <div style={{ padding: '8px 16px', borderBottom: `1px solid ${c.border}`, display: 'flex', alignItems: 'center', gap: '6px' }}>
        {['#f87171','#f59e0b','#10b981'].map(col => (
          <div key={col} style={{ width: '10px', height: '10px', borderRadius: '50%', background: col, opacity: 0.7 }} />
        ))}
        <span style={{ fontSize: '11px', color: c.muted, marginLeft: '6px' }}>Solution.apex</span>
      </div>
      <pre style={{ margin: 0, padding: '18px 20px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {lines}
      </pre>
    </div>
  );
}

// ── Tool cards (expandable) ──────────────────────────────────────────────────
function ToolCards({ tools, challenges, findFirstChallenge, navigate, c }) {
  const [expanded, setExpanded] = useState(null);
  const editorBg = c.theme === 'light' ? '#f8f9fd' : '#070810';

  const toggle = (i) => setExpanded(expanded === i ? null : i);

  const highlightCode = (code) => code.split('\n').map((line, i) => {
    const h = line
      .replace(/(\/\/.*$)/g, '<span style="color:#4b5563">$1</span>')
      .replace(/\b(public|private|static|void|class|return|new|if|else|for|null|true|false|implements|extends|try|catch)\b/g, '<span style="color:#60a5fa">$1</span>')
      .replace(/@\w+/g, '<span style="color:#4f8ef7">$&</span>')
      .replace(/\b(String|Integer|Decimal|Boolean|Id|List|Map|Set|Date|DateTime|Account|Contact)\b/g, '<span style="color:#a5b4fc">$1</span>')
      .replace(/('[^']*')/g, '<span style="color:#86efac">$1</span>');
    return <span key={i} dangerouslySetInnerHTML={{ __html: h + '\n' }} />;
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {tools.map((tool, i) => {
        const isOpen          = expanded === i;
        const hasDetail       = !!tool.explanation;
        const firstChallenge  = findFirstChallenge(tool.range);

        return (
          <div key={i} style={{
            background: c.card,
            border: `1px solid ${isOpen ? c.accent : c.border}`,
            borderRadius: '12px',
            overflow: 'hidden',
            transition: 'border-color 0.2s',
            boxShadow: c.shadow,
          }}>
            {/* Card header — always visible */}
            <div
              onClick={() => hasDetail && toggle(i)}
              style={{
                display: 'flex', alignItems: 'center', gap: '16px',
                padding: '16px 20px',
                cursor: hasDetail ? 'pointer' : 'default',
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => { if (hasDetail) e.currentTarget.style.background = c.cardHover; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>

              {/* Expand indicator */}
              <div style={{
                width: '28px', height: '28px', borderRadius: '8px', flexShrink: 0,
                background: isOpen ? c.accentBg : c.bg,
                border: `1px solid ${isOpen ? c.accent : c.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '12px', color: isOpen ? c.accent : c.muted,
                transition: 'all 0.2s',
              }}>
                {isOpen ? '▲' : '▼'}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px', flexWrap: 'wrap' }}>
                  <code style={{ fontSize: '15px', fontWeight: 700, color: c.accent, fontFamily: 'monospace' }}>{tool.name}</code>
                  <span style={{ fontSize: '10px', color: c.secondary, background: c.accentBg, border: `1px solid ${c.accentBorder}`, padding: '2px 8px', borderRadius: '10px', fontWeight: 500 }}>{tool.range}</span>
                </div>
                <p style={{ fontSize: '13px', color: c.secondary, margin: 0, lineHeight: 1.5 }}>{tool.desc}</p>
              </div>

              {hasDetail && (
                <span style={{ fontSize: '12px', color: c.accent, fontWeight: 500, flexShrink: 0, whiteSpace: 'nowrap' }}>
                  {isOpen ? 'collapse' : 'expand →'}
                </span>
              )}
            </div>

            {/* Expanded content */}
            {isOpen && hasDetail && (
              <div style={{ borderTop: `1px solid ${c.border}`, padding: '24px 24px 20px' }}>

                {/* Explanation */}
                <p style={{ fontSize: '14px', color: c.text, lineHeight: 1.85, margin: '0 0 24px' }}>
                  {tool.explanation}
                </p>

                {/* Key points */}
                {Array.isArray(tool.key_points) && tool.key_points.length > 0 && (
                  <div style={{ marginBottom: '24px' }}>
                    <p style={{ fontSize: '11px', fontWeight: 600, color: c.secondary, textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 12px' }}>Key points</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {tool.key_points.map((point, pi) => (
                        <div key={pi} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                          <div style={{
                            width: '18px', height: '18px', borderRadius: '50%', flexShrink: 0, marginTop: '2px',
                            background: c.accentBg, border: `1px solid ${c.accentBorder}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '9px', color: c.accent, fontWeight: 700,
                          }}>✓</div>
                          <p style={{ fontSize: '13px', color: c.secondary, margin: 0, lineHeight: 1.65 }}>{point}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Code snippet */}
                {tool.code && (
                  <div style={{ marginBottom: '20px' }}>
                    <p style={{ fontSize: '11px', fontWeight: 600, color: c.secondary, textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 10px' }}>Example</p>
                    <div style={{
                      background: editorBg, border: `1px solid ${c.border}`,
                      borderRadius: '8px', overflow: 'auto',
                      fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                      fontSize: '12px', lineHeight: 1.9, color: c.secondary,
                    }}>
                      <pre style={{ margin: 0, padding: '16px 18px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {highlightCode(tool.code)}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Jump to challenges CTA */}
                {firstChallenge && (
                  <button
                    onClick={() => navigate(`/challenge/${firstChallenge.id}`)}
                    style={{
                      background: c.accentBg, border: `1px solid ${c.accent}`,
                      color: c.accent, padding: '8px 18px', borderRadius: '7px',
                      cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                      fontFamily: "'Inter', sans-serif", transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { e.target.style.background = c.accent; e.target.style.color = '#fff'; }}
                    onMouseLeave={e => { e.target.style.background = c.accentBg; e.target.style.color = c.accent; }}>
                    Jump to challenges →
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Section wrapper ──────────────────────────────────────────────────────────
function Section({ id, title, icon, children, c }) {
  return (
    <section id={id} style={{ marginBottom: '64px', scrollMarginTop: '80px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px', paddingBottom: '14px', borderBottom: `1px solid ${c.border}` }}>
        <span style={{ fontSize: '20px' }}>{icon}</span>
        <h2 style={{ fontSize: '20px', fontWeight: 700, color: c.text, margin: 0, letterSpacing: '-0.3px' }}>{title}</h2>
      </div>
      {children}
    </section>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────
export default function LearnPage() {
  const { topic }    = useParams();
  const navigate     = useNavigate();
  const { theme }    = useTheme();
  const c            = { ...TOKENS[theme], theme };

  const decodedTopic = decodeURIComponent(topic);

  const [data, setData]           = useState(null);
  const [subtopics, setSubtopics] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [activeSection, setActive] = useState('overview');
  const [selectedSubtopic, setSelectedSubtopic] = useState(null);
  const contentRef                = useRef(null);

  // Load topic overview and subtopics if applicable
  useEffect(() => {
    setLoading(true);
    fetch(`https://trailcode-backend.onrender.com/topic-overviews/${encodeURIComponent(decodedTopic)}`)
      .then(r => r.json())
      .then(d => {
        setData(d);
        setLoading(false);
        
        // Only fetch subtopics for parent topics
        if (d.topic === 'LWC Fundamentals' || d.topic === 'LWC Advanced') {
          fetch(`https://trailcode-backend.onrender.com/topic-overviews?parent=${encodeURIComponent(d.topic)}`)
            .then(r => r.json())
            .then(subs => {
              const subArray = Array.isArray(subs) ? subs : [];
              // Only keep actual subtopics of this parent (named "LWC Fundamentals - ...")
              const filtered = subArray.filter(s =>
                s.topic?.startsWith(d.topic + ' - ')
              );
              setSubtopics(filtered);
              if (filtered.length > 0) setSelectedSubtopic(filtered[0]);
            })
            .catch(() => setSubtopics([]));
        }
      })
      .catch(() => setLoading(false));
  }, [decodedTopic]);

  // Load challenges for this topic's category
  useEffect(() => {
    fetch('https://trailcode-backend.onrender.com/challenges')
      .then(r => r.json())
      .then(d => setChallenges(d.filter(ch => ch.topic === decodedTopic)));
  }, [decodedTopic]);

  // Scroll spy
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );
    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [data, selectedSubtopic]);

  const findFirstChallenge = (range) => {
    const match = range?.match(/ch\s*(\d+)/);
    if (!match) return null;
    const targetIndex = parseInt(match[1]);
    return challenges.find(ch => ch.order_index === targetIndex);
  };

  const startChallenges = () => {
    const first = challenges[0];
    if (first) navigate(`/challenge/${first.id}`);
    else navigate(`/challenges/apex`);
  };

  // Use selected subtopic if available, otherwise use main data
  const displayData = selectedSubtopic || data;

  // Which sections have data
  const hasSections = displayData ? {
    overview: !!displayData.overview,
    why:      Array.isArray(displayData.why_it_matters) && displayData.why_it_matters.length > 0,
    tools:    Array.isArray(displayData.tools)          && displayData.tools.length > 0,
    code:     !!displayData.code_example,
    gotchas:  Array.isArray(displayData.gotchas)        && displayData.gotchas.length > 0,
    quiz:     Array.isArray(displayData.questions)      && displayData.questions.length > 0,
    docs:     Array.isArray(displayData.doc_links)      && displayData.doc_links.length > 0,
  } : {};

  const visibleSections = SECTIONS.filter(s => hasSections[s.id]);

  if (loading) return (
    <div style={{ background: c.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.25s' }}>
      <p style={{ color: c.secondary, fontSize: '14px' }}>Loading...</p>
    </div>
  );

  if (!data) return (
    <div style={{ background: c.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '16px' }}>
      <p style={{ color: c.text, fontSize: '16px' }}>Topic not found</p>
      <button onClick={() => navigate(-1)} style={{ background: c.accent, color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '7px', cursor: 'pointer', fontWeight: 600 }}>Go back</button>
    </div>
  );

  return (
    <div style={{ background: c.bg, minHeight: '100vh', color: c.text, fontFamily: "'Inter', -apple-system, sans-serif", transition: 'background 0.25s, color 0.25s' }}>

      {/* Navbar */}
      <nav style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '0 clamp(24px, 4vw, 60px)', height: '58px',
        background: c.navBg, backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${c.border}`,
        position: 'sticky', top: 0, zIndex: 100,
        transition: 'background 0.25s, border-color 0.25s',
      }}>
        {/* Breadcrumb */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
          <span style={{ color: c.muted, cursor: 'pointer', transition: 'color 0.15s' }}
            onClick={() => navigate('/')}
            onMouseEnter={e => e.target.style.color = c.text}
            onMouseLeave={e => e.target.style.color = c.muted}>
            TrailCode
          </span>
          <span style={{ color: c.muted }}>›</span>
          <span style={{ color: c.muted, cursor: 'pointer', transition: 'color 0.15s' }}
            onClick={() => navigate(`/challenges/${data.category || 'apex'}`)}
            onMouseEnter={e => e.target.style.color = c.text}
            onMouseLeave={e => e.target.style.color = c.muted}>
            Challenges
          </span>
          <span style={{ color: c.muted }}>›</span>
          <span style={{ color: c.secondary }}>Learn</span>
        </div>

        <div style={{ fontSize: '18px', fontWeight: 800, letterSpacing: '-0.5px', color: c.text, cursor: 'pointer' }}
          onClick={() => navigate('/')}>
          Trail<span style={{ color: c.accent }}>Code</span>
        </div>

        <button onClick={startChallenges} style={{
          background: c.accent, color: '#fff', border: 'none',
          padding: '7px 18px', borderRadius: '7px', cursor: 'pointer',
          fontSize: '13px', fontWeight: 700, fontFamily: "'Inter', sans-serif",
          transition: 'opacity 0.15s',
        }}
          onMouseEnter={e => e.target.style.opacity = '0.85'}
          onMouseLeave={e => e.target.style.opacity = '1'}>
          Start challenges →
        </button>
      </nav>

      {/* Hero strip */}
      <div style={{
        background: c.bgAlt, borderBottom: `1px solid ${c.border}`,
        padding: '36px clamp(24px, 4vw, 60px)',
        transition: 'background 0.25s',
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '10px' }}>
            <span style={{ fontSize: '32px' }}>{TOPIC_ICONS[decodedTopic] || '📖'}</span>
            <div>
              <p style={{ fontSize: '11px', color: c.accent, textTransform: 'uppercase', letterSpacing: '2px', fontWeight: 600, margin: '0 0 4px' }}>Learn</p>
              <h1 style={{ fontSize: '28px', fontWeight: 800, color: c.text, margin: 0, letterSpacing: '-0.5px' }}>{decodedTopic}</h1>
            </div>
          </div>
          <p style={{ fontSize: '14px', color: c.secondary, margin: '0', maxWidth: '600px', lineHeight: 1.7 }}>
            {data.overview?.slice(0, 140)}{data.overview?.length > 140 ? '...' : ''}
          </p>
          <div style={{ display: 'flex', gap: '16px', marginTop: '16px' }}>
            <span style={{ fontSize: '12px', color: c.secondary }}>
              <span style={{ color: c.accent, fontWeight: 600 }}>{challenges.length}</span> challenges
            </span>
            <span style={{ fontSize: '12px', color: c.secondary }}>
              <span style={{ color: c.accent, fontWeight: 600 }}>{(data.gotchas || []).length}</span> gotchas
            </span>
            <span style={{ fontSize: '12px', color: c.secondary }}>
              <span style={{ color: c.accent, fontWeight: 600 }}>{(data.questions || []).length}</span> quiz questions
            </span>
          </div>
        </div>
      </div>

      {/* Subtopics Tabs - Only show if subtopics exist */}
      {subtopics.length > 0 && (
        <div style={{ background: c.bgAlt, borderBottom: `1px solid ${c.border}`, padding: '0 clamp(24px, 4vw, 60px)' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', gap: '0', overflowX: 'auto' }}>
            {subtopics.map((sub) => (
              <button
                key={sub.id}
                onClick={() => setSelectedSubtopic(sub)}
                style={{
                  flex: '0 0 auto',
                  background: 'transparent',
                  border: 'none',
                  padding: '16px 20px',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: selectedSubtopic?.id === sub.id ? c.accent : c.secondary,
                  borderBottom: selectedSubtopic?.id === sub.id ? `3px solid ${c.accent}` : 'none',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  fontFamily: "'Inter', sans-serif",
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={e => { if (selectedSubtopic?.id !== sub.id) e.target.style.color = c.text; }}
                onMouseLeave={e => { if (selectedSubtopic?.id !== sub.id) e.target.style.color = c.secondary; }}>
                {sub.topic.replace('LWC Fundamentals - ', '').replace('LWC Advanced - ', '')}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Body: sidebar + content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 clamp(24px, 4vw, 60px)', display: 'grid', gridTemplateColumns: '220px 1fr', gap: '48px', alignItems: 'start' }}>

        {/* Sidebar */}
        <aside style={{ position: 'sticky', top: '78px', paddingTop: '40px' }}>
          <p style={{ fontSize: '11px', fontWeight: 600, color: c.muted, textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 12px' }}>On this page</p>
          <nav>
            {visibleSections.map(s => (
              <a key={s.id} href={`#${s.id}`}
                onClick={e => { e.preventDefault(); document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth' }); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '7px 12px', borderRadius: '6px', marginBottom: '2px',
                  fontSize: '13px', textDecoration: 'none', cursor: 'pointer',
                  color: activeSection === s.id ? c.accent : c.secondary,
                  background: activeSection === s.id ? c.accentBg : 'transparent',
                  borderLeft: `2px solid ${activeSection === s.id ? c.accent : 'transparent'}`,
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { if (activeSection !== s.id) { e.currentTarget.style.color = c.text; e.currentTarget.style.background = c.card; } }}
                onMouseLeave={e => { if (activeSection !== s.id) { e.currentTarget.style.color = c.secondary; e.currentTarget.style.background = 'transparent'; } }}>
                {s.label}
              </a>
            ))}
          </nav>

          {/* Start challenges CTA in sidebar */}
          <div style={{ marginTop: '32px', padding: '16px', background: c.accentBg, border: `1px solid ${c.accentBorder}`, borderRadius: '10px' }}>
            <p style={{ fontSize: '12px', color: c.accent, fontWeight: 600, margin: '0 0 6px' }}>Ready to practice?</p>
            <p style={{ fontSize: '11px', color: c.secondary, margin: '0 0 12px', lineHeight: 1.5 }}>{challenges.length} challenges waiting</p>
            <button onClick={startChallenges} style={{
              background: c.accent, color: '#fff', border: 'none',
              padding: '8px 14px', borderRadius: '6px', cursor: 'pointer',
              fontSize: '12px', fontWeight: 700, width: '100%',
              fontFamily: "'Inter', sans-serif", transition: 'opacity 0.15s',
            }}
              onMouseEnter={e => e.target.style.opacity = '0.85'}
              onMouseLeave={e => e.target.style.opacity = '1'}>
              Start challenges →
            </button>
          </div>
        </aside>

        {/* Main content */}
        <main ref={contentRef} style={{ paddingTop: '40px', paddingBottom: '80px', minWidth: 0 }}>

          {/* Overview */}
          {hasSections.overview && (
            <Section id="overview" title="Overview" icon="📋" c={c}>
              <p style={{ fontSize: '15px', color: c.text, lineHeight: 1.85, margin: 0 }}>{displayData.overview}</p>
            </Section>
          )}

          {/* Why it matters */}
          {hasSections.why && (
            <Section id="why" title="Why it matters" icon="💡" c={c}>
              <div style={{ borderLeft: `3px solid ${c.accent}`, paddingLeft: '20px' }}>
                {displayData.why_it_matters.map((point, i) => (
                  <p key={i} style={{ fontSize: '14px', color: c.secondary, margin: '0 0 14px', lineHeight: 1.8 }}>
                    <strong style={{ color: c.text }}>{point.bold}</strong>{point.rest}
                  </p>
                ))}
              </div>
            </Section>
          )}

          {/* Tools */}
          {hasSections.tools && (
            <Section id="tools" title="Tools & concepts" icon="🛠️" c={c}>
              <ToolCards tools={displayData.tools} challenges={challenges} findFirstChallenge={findFirstChallenge} navigate={navigate} c={c} />
            </Section>
          )}

          {/* Code example */}
          {hasSections.code && (
            <Section id="code" title="See it in code" icon="💻" c={c}>
              <CodeBlock code={displayData.code_example} c={c} />
            </Section>
          )}

          {/* Gotchas */}
          {hasSections.gotchas && (
            <Section id="gotchas" title="Gotchas before you start" icon="⚠️" c={c}>
              <p style={{ fontSize: '13px', color: c.secondary, margin: '0 0 20px', lineHeight: 1.6 }}>
                Click a card to flip it and see the fix.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
                {displayData.gotchas.map((g, i) => <GotchaCard key={i} g={g} c={c} />)}
              </div>
            </Section>
          )}

          {/* Quiz */}
          {hasSections.quiz && (
            <Section id="quiz" title="Test yourself"icon="🧠" c={c}>
            <TestYourself questions={displayData.questions} c={c} />
          </Section>
        )}

        {/* Docs */}
        {hasSections.docs && (
          <Section id="docs" title="Official docs" icon="📖" c={c}>
            <p style={{ fontSize: '13px', color: c.secondary, margin: '0 0 16px', lineHeight: 1.6 }}>
              Salesforce documentation for this topic.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {displayData.doc_links.map((link, i) => (
                <a key={i} href={link.url} target="_blank" rel="noreferrer" style={{
                  fontSize: '13px', color: c.accent,
                  background: c.accentBg, border: `1px solid ${c.accentBorder}`,
                  padding: '8px 16px', borderRadius: '7px', textDecoration: 'none',
                  fontWeight: 500, transition: 'all 0.15s',
                  display: 'flex', alignItems: 'center', gap: '6px',
                }}
                  onMouseEnter={e => { e.currentTarget.style.background = c.accent; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = c.accent; }}
                  onMouseLeave={e => { e.currentTarget.style.background = c.accentBg; e.currentTarget.style.color = c.accent; e.currentTarget.style.borderColor = c.accentBorder; }}>
                  📄 {link.label} →
                </a>
              ))}
            </div>
          </Section>
        )}

        {/* Bottom CTA */}
        <div style={{
          background: c.accentBg, border: `1px solid ${c.accentBorder}`,
          borderRadius: '14px', padding: '32px', textAlign: 'center',
        }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>⚡</div>
          <h3 style={{ fontSize: '20px', fontWeight: 700, color: c.text, margin: '0 0 8px', letterSpacing: '-0.3px' }}>
            Ready to practice {displayData.topic}?
          </h3>
          <p style={{ fontSize: '14px', color: c.secondary, margin: '0 0 24px', lineHeight: 1.6 }}>
            {challenges.length} challenges waiting. Your Salesforce org is the sandbox.
          </p>
          <button onClick={startChallenges} style={{
            background: c.accent, color: '#fff', border: 'none',
            padding: '13px 32px', borderRadius: '8px', cursor: 'pointer',
            fontSize: '15px', fontWeight: 700, fontFamily: "'Inter', sans-serif",
            transition: 'opacity 0.15s, transform 0.12s',
            boxShadow: '0 0 24px rgba(79,142,247,0.3)',
          }}
            onMouseEnter={e => { e.target.style.opacity = '0.88'; e.target.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { e.target.style.opacity = '1'; e.target.style.transform = 'translateY(0)'; }}>
            Start challenges →
          </button>
        </div>
      </main>
    </div>
  </div>
);
}